import React, { useEffect, useRef, useState } from 'react';
import useLhcFormsScripts from '../../hooks/useLhcFormsScripts';

/**
 * LHCFormViewer - Read-only viewer for LHC form schemas (admin preview)
 * Renders LHC forms using LForms library without submission capabilities
 *
 * @param {Object} schema - LHC form schema object
 */
const LHCFormViewer = ({ schema }) => {
  const lhcFormRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [error, setError] = useState(null);

  // Load LHC Forms scripts dynamically
  const { isLoaded: lhcScriptsLoaded, error: lhcScriptsError } = useLhcFormsScripts();

  // Effect to initialize the LHC form
  useEffect(() => {
    if (schema && lhcFormRef.current && lhcScriptsLoaded && window.LForms) {
      // Prevent re-initialization
      if (isInitializedRef.current) {
        return;
      }

      try {
        // Clear previous form content if any
        if (lhcFormRef.current) {
          lhcFormRef.current.innerHTML = '';
        }

        // Set FHIR context before using any FHIR-related functions
        if (window.LForms.Util.setFHIRContext) {
          const fhirVersion = schema?.fhirVersion || 'R4';
          window.LForms.Util.setFHIRContext(fhirVersion);
        }

        // Use addFormToPage to render the form
        try {
          const promise = window.LForms.Util.addFormToPage(schema, lhcFormRef.current, {});

          // Handle the promise if it exists
          if (promise && typeof promise.then === 'function') {
            promise.then(() => {
              isInitializedRef.current = true;
            }).catch(error => {
              setError(`Error rendering form: ${error.message}`);
            });
          } else {
            isInitializedRef.current = true;
          }

        } catch (renderError) {
          setError(`Error rendering form: ${renderError.message}`);
        }

        // Add event listener for changes (for read-only mode, we just track changes without submission)
        const handleChange = () => {
          if (window.LForms && window.LForms.Util) {
            if (window.LForms.Util.getFormData) {
              window.LForms.Util.getFormData(lhcFormRef.current, false, false);
            }
          }
        };

        lhcFormRef.current.addEventListener('change', handleChange);

        // Cleanup function to remove event listener
        return () => {
          const formElement = lhcFormRef.current;
          if (formElement) {
            formElement.removeEventListener('change', handleChange);
          }
        };
      } catch (error) {
        setError(`Error initializing form: ${error.message}`);
      }
    }
  }, [schema, lhcScriptsLoaded]);

  // Reset initialization flag when schema changes
  useEffect(() => {
    isInitializedRef.current = false;
  }, [schema]);

  if (lhcScriptsError || error) {
    return (
      <div className="card p-3 mb-3">
        <div className="alert alert-danger">
          {lhcScriptsError || error}
        </div>
      </div>
    );
  }

  if (!schema || Object.keys(schema).length === 0) {
    return (
      <div className="card p-3 mb-3">
        <div className="alert alert-info">
          No form schema available to preview.
        </div>
      </div>
    );
  }

  return (
    <div className="card p-3 mb-3">
      <h5 className="mb-3">Form Preview</h5>
      {lhcScriptsLoaded ? (
        <div ref={lhcFormRef} id="lhc-form-viewer-container" style={{ minHeight: '300px' }}></div>
      ) : (
        <div className="text-center p-3">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading form component...</span>
          </div>
          <p className="mt-2 text-muted">Initializing LHC Forms...</p>
        </div>
      )}
    </div>
  );
};

export default LHCFormViewer;
